import { useCallback, useMemo, useState } from 'react';

interface UseSearchableLimitedOptionsConfig<TOption> {
  getSearchText?: (option: TOption) => string;
  options: readonly TOption[];
  visibleLimit?: number;
}

const defaultGetSearchText = <TOption,>(option: TOption) => String(option);

export const useSearchableLimitedOptions = <TOption,>({
  getSearchText = defaultGetSearchText,
  options,
  visibleLimit,
}: UseSearchableLimitedOptionsConfig<TOption>) => {
  const [searchValue, setSearchValue] = useState('');
  const [showAllOptions, setShowAllOptions] = useState(false);
  const normalizedSearchValue = searchValue.trim().toLowerCase();
  const isSearching = normalizedSearchValue.length > 0;
  const filteredOptions = useMemo(
    () => (isSearching
      ? options.filter(option => getSearchText(option).toLowerCase().includes(normalizedSearchValue))
      : options),
    [getSearchText, isSearching, normalizedSearchValue, options]
  );
  const shouldLimitOptions = typeof visibleLimit === 'number' && visibleLimit >= 0 && !isSearching && !showAllOptions;
  const visibleOptions = useMemo(
    () => (shouldLimitOptions ? filteredOptions.slice(0, visibleLimit) : filteredOptions),
    [filteredOptions, shouldLimitOptions, visibleLimit]
  );
  const hiddenOptionCount = typeof visibleLimit === 'number'
    ? Math.max(filteredOptions.length - visibleLimit, 0)
    : 0;
  const hasMoreOptions = Boolean(shouldLimitOptions && hiddenOptionCount > 0);
  const canShowFewerOptions = Boolean(
    typeof visibleLimit === 'number'
      && !isSearching
      && showAllOptions
      && options.length > visibleLimit
  );
  const resetSearchableOptions = useCallback(() => {
    setSearchValue('');
    setShowAllOptions(false);
  }, []);

  return {
    canShowFewerOptions,
    filteredOptions,
    hasMoreOptions,
    hiddenOptionCount,
    isSearching,
    resetSearchableOptions,
    searchValue,
    setSearchValue,
    setShowAllOptions,
    showAllOptions,
    visibleOptions,
  };
};
